import { BEIGE_300, BEIGE_700, BOX_SHADOW_XL, GRAY_200, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { Box, Divider, Drawer, DrawerProps, Stack, SxProps, Theme, Typography, backdropClasses, drawerClasses, styled } from '@mui/material'
import { FC, ReactNode, useEffect, useRef } from 'react'

import { Close } from '@mui/icons-material'
import styles from './ResponsiveDrawer.module.sass'
import { useBottomActionBarRef } from 'utils/hooks/bottomActionBarRegistry'
import { YIELDS_FOCUS_ATTRIBUTE, useHasOpenPortalModal } from 'utils/hooks/portalModalRegistry'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'
import { MUIIconButton } from 'components/common/MUIIconButton'

interface Props {
  /** Whether the drawer is open or not */
  isOpen: boolean
  /** OnClick action to the drawer */
  onClose: () => void
  /** Drawer title */
  title: ReactNode
  /** Custom components in subTitle section */
  subTitleSlot?: ReactNode
  /** Drawer show up anchor */
  anchor?: DrawerProps['anchor']
  /** Drawer layout breakpoints */
  breakpointQuery?: string
  /** Whether using default close button or not */
  useDefaultCloseButton?: boolean
  /** Custom components in action section */
  actionsSlot?: ReactNode
  /** Custom components rendered in the header, before the default close button. */
  headerActionsSlot?: ReactNode
  /** Drawer height in small size */
  height?: string
  /** Drawer width for left/right anchors */
  width?: string
  /** Custom sx for the header section */
  headerSx?: Record<string, unknown>
  /** Custom sx for the scrollable content area (overrides default p="2rem") */
  contentSx?: Record<string, unknown>
  /** Custom sx for the footer/actions section */
  footerSx?: Record<string, unknown>
  /** Backdrop template to use. Omit for no backdrop. Defaults to 'dark' when enabled. */
  backdrop?: 'dark' | 'blurred'
  /** Whether to disable closing on backdrop click */
  disableBackdropClick?: boolean
  /** Optional sx forwarded to the root StyledDrawer — use to override zIndex
   *  when stacking multiple drawers (e.g. property selector above product drawer). */
  sx?: SxProps<Theme>
  /**
   * When true, this drawer does not displace the global activeDrawer when it
   * opens, and does not register itself as activeDrawer. Use for drawers that
   * intentionally layer on top of another drawer (e.g. the property-selection
   * drawer that opens from inside the product drawer's "Select property first"
   * gate). Default false preserves the singleton behaviour for normal flows.
   */
  allowStacking?: boolean
  /** Dialog semantics on the panel: `role="dialog"`, `aria-modal`, named by `title`. Focus trap and Escape are MUI's */
  isAccessibleDialog?: boolean
  children: ReactNode
}

/** Shared beige header style for gallery drawers */
export const GALLERY_DRAWER_HEADER_SX = {
  backgroundColor: BEIGE_300,
  borderBottom: `1px solid ${BEIGE_700}`
} as const

/** MUI Drawer extension props */
interface MUIDrawerProps extends DrawerProps {
  height?: string
  paperWidth?: string
}

/**
 * MUI Drawer component with additional styles
 */
const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'paperWidth'
})<MUIDrawerProps>(({ anchor, height, paperWidth }) => {
  return {
    [`.${drawerClasses.paper}`]: {
      boxSizing: 'border-box',
      height: anchor === 'bottom' ? height : '100%',
      boxShadow: BOX_SHADOW_XL,
      borderRadius: anchor === 'bottom' ? '2rem 2rem 0 0' : '',
      pointerEvents: 'auto',

      [`&.${drawerClasses.paperAnchorBottom}`]: {
        height,
        width: 'auto',
      },
      [`&.${drawerClasses.paperAnchorLeft}`]: {
        width: paperWidth || '400px'
      },
      [`&.${drawerClasses.paperAnchorRight}`]: {
        width: paperWidth || '400px'
      },
    },
    [`.${backdropClasses.root}`]: {
      background: 'unset',
    },
  }
})

/** Tracks the currently open drawer so opening a new one closes the previous */
let activeDrawer: { id: symbol, close: () => void } | null = null

// Not React's useId: its counter is global, and the storyshots keep the ids it generates verbatim
let generatedTitleIds = 0

/**
 * Drawer component for containing content that needs to be accessed only after some interaction.
 * Utilizes MUI Drawer component but automatically switches orientation.
 *
 * @example
 * <ResponsiveDrawer title="Struggles of the Dark Lord" isOpen={true} onClose={() => alert('I want my nose back :/')}>
 *  <h5>Harry Potter</h5>
 *  <p>the boy who lived, comes to die</p>
 *  <h1>AAAAAVADA KEDAVRA!</h1>
 * </ResponsiveDrawer>
 */
export const ResponsiveDrawer: FC<Props> = ({
  children,
  isOpen,
  onClose,
  title,
  anchor = 'right',
  breakpointQuery = MOBILE_VIEW_WIDTH_QUERY,
  useDefaultCloseButton = true,
  height = '90vh',
  width,
  headerSx,
  contentSx,
  footerSx,
  actionsSlot,
  headerActionsSlot,
  subTitleSlot,
  backdrop,
  disableBackdropClick = false,
  sx,
  allowStacking = false,
  isAccessibleDialog = false,
}) => {

  const { t } = useTranslation('common')

  const isSmall = useMediaQuery(breakpointQuery)
  const bottomActionBarRef = useBottomActionBarRef<HTMLDivElement>()
  // A portal modal on top sits outside this drawer's DOM node, so the drawer's focus trap would
  // otherwise pull every keystroke aimed at it back into the drawer.
  const hasOpenPortalModal = useHasOpenPortalModal()
  const instanceId = useRef(Symbol('drawer')).current
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const titleIdRef = useRef('')
  if (isAccessibleDialog && !titleIdRef.current) titleIdRef.current = `responsive-drawer-title-${++generatedTitleIds}`
  const titleId = isAccessibleDialog && !!title ? titleIdRef.current : undefined

  useEffect(() => {
    if (allowStacking) return
    if (isOpen) {
      if (activeDrawer && activeDrawer.id !== instanceId) {
        activeDrawer.close()
      }
      activeDrawer = { id: instanceId, close: () => onCloseRef.current() }
    }
    // Forget this drawer when it closes AND when it unmounts while open: a route change can unmount its host in
    // the same commit that closes it (the TopBar's property drawer on the way into the purchase flow, QA-458), so
    // a close-only reset left a stale entry whose onClose the next drawer to open would run.
    return () => {
      if (activeDrawer?.id === instanceId) activeDrawer = null
    }
  }, [isOpen, instanceId, allowStacking])

  return (
    <StyledDrawer
      className={styles.drawer}
      anchor={isSmall ? 'bottom' : anchor}
      open={isOpen}
      onClose={disableBackdropClick ? (_event, reason) => { if (reason !== 'backdropClick') onClose() } : onClose}
      elevation={10}
      height={height}
      disableScrollLock
      disableEnforceFocus={hasOpenPortalModal}
      {...(hasOpenPortalModal && { [YIELDS_FOCUS_ATTRIBUTE]: true })}
      paperWidth={width}
      PaperProps={isAccessibleDialog ? { role: 'dialog', 'aria-modal': true, 'aria-labelledby': titleId } : undefined}
      sx={[
        {
          ...(disableBackdropClick && { pointerEvents: 'none' }),
          backgroundColor: backdrop === 'dark'
            ? 'rgba(37, 42, 46, 0.95)'
            : backdrop === 'blurred' ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
          backdropFilter: backdrop ? 'blur(.2rem)' : 'none',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Stack direction="column" height="100%">

        <Box sx={{
          borderBottom: `1px solid ${GRAY_200}`,
          ...headerSx
        }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="2rem"
            className={styles.topBar}
            sx={{
              padding: subTitleSlot ? '1.6rem 2.4rem 0' : '1.6rem 2.4rem 1.2rem'
            }}
          >
            {!!title &&
              <Typography id={titleId} variant="text-xl" fontWeight="550" sx={{ overflowWrap: 'anywhere' }}>
                {title}
              </Typography>
            }
            <Stack direction="row" alignItems="center" gap="0.8rem">
              {headerActionsSlot}
              {useDefaultCloseButton &&
                <MUIIconButton icon={<Close />} ariaLabel={t('close')} onClick={onClose} />
              }
            </Stack>
          </Stack>
          {!!subTitleSlot &&
            <Stack
              direction="row"
              justifyContent="start"
              sx={{
                padding: '.2rem 2.4rem 1.2rem'
              }}
            >
              {subTitleSlot}
            </Stack>
          }
        </Box>

        <Box
          flex="1 1 20rem"
          overflow="auto"
          boxSizing="border-box"
          p="2rem"
          sx={contentSx}
        >
          {children}
        </Box>

        {!!actionsSlot &&
          <Box ref={bottomActionBarRef}>
            <Divider />
            <Stack
              p="1rem 2rem"
              spacing={1}
              direction="row"
              flexWrap="wrap"
              justifyContent="flex-end"
              sx={footerSx}
            >
              {actionsSlot}
            </Stack>
          </Box>
        }

      </Stack>
    </StyledDrawer>
  )
}
